import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { History, X } from "lucide-react";
import { payrollOpsService } from "../../../services/api";
import useConfirmDialog from "../../../hooks/useConfirmDialog";
import { DsIcon } from "../../../design-system";

function formatAuditWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString();
}

function FinaliseHistoryPopup({ darkMode, open, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await payrollOpsService.listFinaliseAudits();
        if (!cancelled) setRows(Array.isArray(res?.data) ? res.data : []);
      } catch (err) {
        if (!cancelled) {
          setRows([]);
          toast.error(err?.response?.data?.message || err?.message || "Could not load history");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
      <div
        className={`flex max-h-[80vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border shadow-[var(--ds-shadow-card)] ${
          darkMode
            ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
            : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
        }`}
      >
        <div className="flex items-center justify-between border-b border-[hsl(var(--ds-border))] px-4 py-3">
          <h3 className="ds-h4">Finalise / Unfinalise history</h3>
          <button type="button" onClick={onClose} className="rounded p-1 text-[hsl(var(--ds-muted-fg))]">
            <DsIcon icon={X} size="sm" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-3">
          {loading ? (
            <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">No finalise or unlock actions recorded yet.</p>
          ) : (
            <table className="ds-table w-full text-left text-xs">
              <thead className="sticky top-0 bg-[hsl(var(--ds-muted))]">
                <tr>
                  <th className="px-2 py-2 font-semibold">Pay month</th>
                  <th className="px-2 py-2 font-semibold">Action</th>
                  <th className="px-2 py-2 font-semibold">By</th>
                  <th className="px-2 py-2 font-semibold">Date / time</th>
                  <th className="px-2 py-2 font-semibold">Note</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-[hsl(var(--ds-border))]">
                    <td className="px-2 py-2 whitespace-nowrap">{r.payMonthLabel || r.payMonth}</td>
                    <td className="px-2 py-2">{r.actionLabel || r.action}</td>
                    <td className="px-2 py-2">{r.actorName || "—"}</td>
                    <td className="px-2 py-2 whitespace-nowrap">{formatAuditWhen(r.createdAt)}</td>
                    <td className="px-2 py-2">{r.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export function FinalisePayrollActions({
  darkMode,
  run,
  stepsFullyLocked,
  canApprove = true,
  onUpdated,
}) {
  const [busy, setBusy] = useState(false);
  const [disb, setDisb] = useState(null);
  const [disbLoading, setDisbLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const { confirm, dialogNode } = useConfirmDialog();
  const finalized = !!run?.finalizedAt;
  const unlockPending = run?.unlockStatus === "pending_approval";

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!run?.payMonth || finalized) {
        setDisb(null);
        return;
      }
      setDisbLoading(true);
      try {
        const res = await payrollOpsService.listDisbursements(run.payMonth);
        if (!cancelled) setDisb(res?.data || null);
      } catch {
        if (!cancelled) setDisb(null);
      } finally {
        if (!cancelled) setDisbLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [run?.payMonth, run?.id, finalized]);

  const disbStats = useMemo(() => {
    const rows = Array.isArray(disb?.rows) ? disb.rows : [];
    const totals = disb?.totals || {};
    const pending =
      totals.pendingCount != null
        ? Number(totals.pendingCount)
        : rows.filter((r) => String(r.status || "pending") === "pending").length;
    const total = totals.totalCount != null ? Number(totals.totalCount) : rows.length;
    const settled = Math.max(0, total - pending);
    return { pending, total, settled, allSettled: total > 0 && pending === 0 };
  }, [disb]);

  const historyBtn = (
    <button
      type="button"
      onClick={() => setHistoryOpen(true)}
      className="inline-flex items-center gap-1 rounded border border-[hsl(var(--ds-border))] px-3 py-1.5 text-xs"
    >
      <DsIcon icon={History} size="sm" />
      History
    </button>
  );

  const finalise = async () => {
    if (!run?.id) return;
    const pendingNote =
      disbStats.pending > 0
        ? `\n\n${disbStats.pending} unpaid/recovery obligation(s) will remain open and carry forward.`
        : "";
    const ok = await confirm({
      title: "Finalise payroll month",
      message: `Finalise this pay month? Payroll calculation will be locked. Payment register stays open for later payouts.${pendingNote}`,
      confirmText: "Finalise",
      cancelText: "Cancel",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await payrollOpsService.finaliseRun(run.id);
      toast.success(res?.message || "Finalised");
      onUpdated?.(res?.data);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Finalise failed");
    } finally {
      setBusy(false);
    }
  };

  const requestUnlock = async () => {
    if (!run?.id) return;
    setBusy(true);
    try {
      const res = await payrollOpsService.requestUnlockRun(run.id);
      toast.success(res?.message || "Unlock requested");
      onUpdated?.(res?.data);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Unlock failed");
    } finally {
      setBusy(false);
    }
  };

  const approveUnlock = async (approve) => {
    if (!run?.id) return;
    setBusy(true);
    try {
      const res = await payrollOpsService.approveUnlockRun(run.id, approve);
      toast.success(res?.message || (approve ? "Unlocked" : "Rejected"));
      onUpdated?.(res?.data);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  if (!canApprove) {
    return (
      <>
        {historyBtn}
        <FinaliseHistoryPopup darkMode={darkMode} open={historyOpen} onClose={() => setHistoryOpen(false)} />
      </>
    );
  }

  if (finalized) {
    if (unlockPending) {
      return (
        <>
          {historyBtn}
          <button
            type="button"
            disabled={busy}
            onClick={() => approveUnlock(true)}
            className="rounded bg-emerald-600 px-3 py-1.5 text-xs text-white disabled:opacity-50"
          >
            Approve unlock
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => approveUnlock(false)}
            className={`rounded border px-3 py-1.5 text-xs disabled:opacity-50 ${
              darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
            }`}
          >
            Reject
          </button>
          <FinaliseHistoryPopup darkMode={darkMode} open={historyOpen} onClose={() => setHistoryOpen(false)} />
        </>
      );
    }
    return (
      <>
        {historyBtn}
        <button
          type="button"
          disabled={busy}
          onClick={requestUnlock}
          className="rounded border border-amber-400 px-3 py-1.5 text-xs font-medium text-amber-900 disabled:opacity-50"
        >
          Request unlock
        </button>
        <FinaliseHistoryPopup darkMode={darkMode} open={historyOpen} onClose={() => setHistoryOpen(false)} />
      </>
    );
  }

  return (
    <>
      {dialogNode}
      {historyBtn}
      <button
        type="button"
        disabled={busy || disbLoading}
        onClick={finalise}
        title={
          disbStats.pending > 0
            ? `Finalise with ${disbStats.pending} unpaid/recovery open — they carry forward`
            : "Finalise and lock this pay month"
        }
        className="rounded bg-[hsl(var(--ds-primary))] px-4 py-1.5 text-xs font-medium text-white disabled:opacity-50"
      >
        Finalise payroll
      </button>
      <FinaliseHistoryPopup darkMode={darkMode} open={historyOpen} onClose={() => setHistoryOpen(false)} />
    </>
  );
}

export default function PayrollFinalisePanel({
  darkMode,
  run,
  stepsFullyLocked,
  canApprove = true,
}) {
  const [disb, setDisb] = useState(null);
  const [disbLoading, setDisbLoading] = useState(false);
  const finalized = !!run?.finalizedAt;
  const unlockPending = run?.unlockStatus === "pending_approval";

  const border = darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]";
  const muted = darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]";

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!run?.payMonth || finalized) {
        setDisb(null);
        return;
      }
      setDisbLoading(true);
      try {
        const res = await payrollOpsService.listDisbursements(run.payMonth);
        if (!cancelled) setDisb(res?.data || null);
      } catch {
        if (!cancelled) setDisb(null);
      } finally {
        if (!cancelled) setDisbLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [run?.payMonth, run?.id, finalized]);

  const disbStats = useMemo(() => {
    const rows = Array.isArray(disb?.rows) ? disb.rows : [];
    const totals = disb?.totals || {};
    const pending =
      totals.pendingCount != null
        ? Number(totals.pendingCount)
        : rows.filter((r) => String(r.status || "pending") === "pending").length;
    const total = totals.totalCount != null ? Number(totals.totalCount) : rows.length;
    const settled = Math.max(0, total - pending);
    return { pending, total, settled, allSettled: total > 0 && pending === 0 };
  }, [disb]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className={`rounded-lg border px-3 py-3 text-sm ${border}`}>
        {finalized ? (
          <>
            <p className="font-medium text-emerald-700 dark:text-emerald-300">
              This pay month is finalised and locked.
            </p>
            <p className={`mt-1 text-xs ${muted}`}>
              Unlock is allowed only when the next month is already unfinalised (or has no payroll
              run). Unfinalised months must stay a contiguous tail — you cannot skip an earlier month.
            </p>
            {!stepsFullyLocked && (
              <p className={`mt-2 text-xs ${muted}`}>
                Some employees in the current scope are still unprocessed. That does not block unlock.
              </p>
            )}
            {unlockPending && (
              <p className="mt-2 text-xs text-amber-700">Unlock pending approval</p>
            )}
          </>
        ) : (
          <>
            {!stepsFullyLocked ? (
              <p className={darkMode ? "text-amber-200" : "text-amber-900"}>
                Some employees in scope are still unprocessed. You can still settle disbursement for
                processed employees and finalise when ready.
              </p>
            ) : (
              <p>All in-scope employees are processed.</p>
            )}
            {disbLoading ? (
              <p className={`mt-2 text-xs ${muted}`}>Checking disbursement…</p>
            ) : disbStats.total === 0 ? (
              <p className={`mt-2 text-xs ${darkMode ? "text-amber-200" : "text-amber-900"}`}>
                No disbursement rows yet. You can still finalise if employees are processed — unpaid
                will appear when the register is loaded.
              </p>
            ) : !disbStats.allSettled ? (
              <p className={`mt-2 text-xs ${darkMode ? "text-amber-200" : "text-amber-900"}`}>
                Disbursement open: {disbStats.settled}/{disbStats.total} settled. Remaining{" "}
                {disbStats.pending} unpaid/recovery will carry forward — Finalise is still allowed.
              </p>
            ) : (
              <p className={`mt-2 text-xs ${darkMode ? "text-emerald-300" : "text-emerald-800"}`}>
                Disbursement complete ({disbStats.settled}/{disbStats.total}). You can finalise to
                lock the company month.
              </p>
            )}
            <p className={`mt-2 text-xs ${muted}`}>
              After finalise, payment register stays open for batch pay / F&amp;F later. You cannot
              finalise this month while an earlier opened month is still unfinalised.
            </p>
            {!canApprove && (
              <p className={`mt-2 text-xs ${muted}`}>You do not have permission to finalise.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
